import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  calculateTransferDates,
  createSelfTransferRequest,
  fetchHodList,
  fetchReasonForTransfer,
  fetchSelfTransferFormPrefill,
  fetchTransferGrounds,
  fetchTransferPeriodOptions,
  fetchTransferUnderList,
  getLoggedInEmpId,
  getLoggedInUser,
} from "./selfTransfer.service";

interface EmployeeInfo {
  EmpID: string;
  EmployeeName: string;
  DesignationNameOfPostHeldPresently: string;
  DivisionUnitOfficeInWhichTheEmployee: string;
  Category: string;
  PayScale: string;
  CustomerSpecificStatus: string;
  EmploymentStatus: string;
  EmployeeSubGroup: string;
  Wing: string;
}

const SelfTransferCreate: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Master options
  const [transferGroundOptions, setTransferGroundOptions] = useState<any[]>([]);
  const [reasonOptions, setReasonOptions] = useState<any[]>([]);
  const [periodUnitOptions, setPeriodUnitOptions] = useState<any[]>([]);
  const [hodOptions, setHodOptions] = useState<any[]>([]);
  const [transferUnderOptions, setTransferUnderOptions] = useState<any[]>([]);
  const [dynamicLoading, setDynamicLoading] = useState(false);

  // Form state
  const [transferGround, setTransferGround] = useState("INTRA");
  const [selectedHod, setSelectedHod] = useState<any>(null);
  const [selectedTransferUnder, setSelectedTransferUnder] = useState<any>(null);
  const [reasonForTransfer, setReasonForTransfer] = useState("PER");
  const [transferPeriodValue, setTransferPeriodValue] = useState("0");
  const [transferPeriodUnit, setTransferPeriodUnit] = useState("MONTH");
  const [remarks, setRemarks] = useState("");

  // Employee profile info
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo>({
    EmpID: "",
    EmployeeName: "",
    DesignationNameOfPostHeldPresently: "",
    DivisionUnitOfficeInWhichTheEmployee: "",
    Category: "Executive",
    PayScale: "",
    CustomerSpecificStatus: "",
    EmploymentStatus: "Active",
    EmployeeSubGroup: "",
    Wing: "",
  });

  // Errors state
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // 1. Initial Load: Masters + Prefill
  useEffect(() => {
    let isMounted = true;
    const initData = async () => {
      try {
        setLoading(true);
        const [groundsRes, reasonsRes, periodsRes, prefillRes] = await Promise.all([
          fetchTransferGrounds(),
          fetchReasonForTransfer(),
          fetchTransferPeriodOptions(),
          fetchSelfTransferFormPrefill(),
        ]);

        if (!isMounted) return;

        if (groundsRes.success && Array.isArray(groundsRes.data)) {
          const seen = new Set<string>();
          const uniqueGrounds = groundsRes.data.filter((item: any) => {
            const key = String(item.TransferGround || item.value || "").trim().toUpperCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setTransferGroundOptions(
            uniqueGrounds.length > 0
              ? uniqueGrounds
              : [
                  { TransferGround: "INTRA", TransferGroundText: "Intra Department" },
                  { TransferGround: "INTER", TransferGroundText: "Inter Department" },
                ]
          );
        } else {
          setTransferGroundOptions([
            { TransferGround: "INTRA", TransferGroundText: "Intra Department" },
            { TransferGround: "INTER", TransferGroundText: "Inter Department" },
          ]);
        }
        if (reasonsRes.success && Array.isArray(reasonsRes.data)) {
          const seen = new Set<string>();
          const uniqueReasons = reasonsRes.data
            .map((r: any) => {
              const rawVal = String(
                r.ReasonForTransfer || r.Value || r.DomvalueL || r.value || r.code || ""
              ).trim().toUpperCase();
              const val =
                rawVal === "TEMPORARY" || rawVal === "TEMP"
                  ? "TEM"
                  : rawVal === "PERMANENT" || rawVal === "PERM"
                  ? "PER"
                  : rawVal;
              const text =
                r.ReasonForTransferText ||
                r.Ddtext ||
                r.label ||
                r.text ||
                (val === "PER" ? "Permanent" : val === "TEM" ? "Temporary" : val);
              return {
                ReasonForTransfer: val,
                ReasonForTransferText: text,
                Value: val,
                Ddtext: text,
              };
            })
            .filter((r: any) => {
              if (!r.ReasonForTransfer || seen.has(r.ReasonForTransfer)) return false;
              seen.add(r.ReasonForTransfer);
              return true;
            });
          setReasonOptions(
            uniqueReasons.length > 0
              ? uniqueReasons
              : [
                  { ReasonForTransfer: "PER", ReasonForTransferText: "Permanent", Value: "PER", Ddtext: "Permanent" },
                  { ReasonForTransfer: "TEM", ReasonForTransferText: "Temporary", Value: "TEM", Ddtext: "Temporary" },
                ]
          );
        } else {
          setReasonOptions([
            { ReasonForTransfer: "PER", ReasonForTransferText: "Permanent", Value: "PER", Ddtext: "Permanent" },
            { ReasonForTransfer: "TEM", ReasonForTransferText: "Temporary", Value: "TEM", Ddtext: "Temporary" },
          ]);
        }

        if (periodsRes.success && Array.isArray(periodsRes.data)) {
          const seen = new Set<string>();
          const uniquePeriods = periodsRes.data
            .map((p: any) => {
              const rawVal = String(
                p.TransferPeriod || p.Value || p.DomvalueL || p.value || ""
              ).trim().toUpperCase();
              let val = rawVal;
              if (rawVal.startsWith("Y")) val = "YEAR";
              else if (rawVal.startsWith("M")) val = "MONTH";
              else if (rawVal.startsWith("D")) val = "DAY";
              const text =
                p.TransferPeriodText ||
                p.Ddtext ||
                p.label ||
                p.text ||
                (val === "YEAR" ? "Year(s)" : val === "MONTH" ? "Month(s)" : val === "DAY" ? "Day(s)" : val);
              return {
                TransferPeriod: val,
                TransferPeriodText: text,
                Value: val,
                Ddtext: text,
              };
            })
            .filter((p: any) => {
              if (!p.TransferPeriod || seen.has(p.TransferPeriod)) return false;
              seen.add(p.TransferPeriod);
              return true;
            });
          setPeriodUnitOptions(
            uniquePeriods.length > 0
              ? uniquePeriods
              : [
                  { TransferPeriod: "YEAR", TransferPeriodText: "Year(s)", Value: "YEAR", Ddtext: "Year(s)" },
                  { TransferPeriod: "MONTH", TransferPeriodText: "Month(s)", Value: "MONTH", Ddtext: "Month(s)" },
                  { TransferPeriod: "DAY", TransferPeriodText: "Day(s)", Value: "DAY", Ddtext: "Day(s)" },
                ]
          );
        } else {
          setPeriodUnitOptions([
            { TransferPeriod: "YEAR", TransferPeriodText: "Year(s)", Value: "YEAR", Ddtext: "Year(s)" },
            { TransferPeriod: "MONTH", TransferPeriodText: "Month(s)", Value: "MONTH", Ddtext: "Month(s)" },
            { TransferPeriod: "DAY", TransferPeriodText: "Day(s)", Value: "DAY", Ddtext: "Day(s)" },
          ]);
        }

        const user = getLoggedInUser();
        const details = user?.details || {};
        const fallbackEmpId = getLoggedInEmpId();

        if (prefillRes.success && prefillRes.data) {
          const d = prefillRes.data;
          setEmployeeInfo({
            EmpID: d.EmpID || fallbackEmpId,
            EmployeeName:
              d.EmployeeName ||
              [details.firstname, details.lastname || details.lsastname].filter(Boolean).join(" ") ||
              "-",
            DesignationNameOfPostHeldPresently:
              d.DesignationNameOfPostHeldPresently ||
              d.Designation ||
              details?.sap_department_designation_wings?.designation_name ||
              "-",
            DivisionUnitOfficeInWhichTheEmployee:
              d.DivisionUnitOfficeInWhichTheEmployee ||
              d.Division ||
              details?.sap_department_designation_wings?.department_name ||
              "-",
            Category: d.Category || "Executive",
            PayScale: d.PayScale || details.pay_scale || "-",
            CustomerSpecificStatus: d.CustomerSpecificStatus || "-",
            EmploymentStatus: d.EmploymentStatus || "Active",
            EmployeeSubGroup: d.EmployeeSubGroup || "-",
            Wing: d.Wing || details?.sap_department_designation_wings?.wing_name || "-",
          });
        } else {
          // Local fallback from logged in user
          setEmployeeInfo({
            EmpID: fallbackEmpId,
            EmployeeName:
              [details.firstname, details.lastname || details.lsastname].filter(Boolean).join(" ") ||
              "-",
            DesignationNameOfPostHeldPresently:
              details?.sap_department_designation_wings?.designation_name || "-",
            DivisionUnitOfficeInWhichTheEmployee:
              details?.sap_department_designation_wings?.department_name || "-",
            Category: "Executive",
            PayScale: details.pay_scale || "-",
            CustomerSpecificStatus: "-",
            EmploymentStatus: "Active",
            EmployeeSubGroup: "-",
            Wing: details?.sap_department_designation_wings?.wing_name || "-",
          });
        }
      } catch (err: any) {
        console.error("Failed to initialize Self Transfer form:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initData();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Dynamic HOD & Transfer Under load when transferGround changes
  const loadDynamicLists = useCallback(async (ground: string) => {
    try {
      setDynamicLoading(true);
      if (ground === "INTER") {
        const hodRes = await fetchHodList("INTER");
        if (hodRes.success && Array.isArray(hodRes.data)) {
          setHodOptions(hodRes.data);
        } else {
          setHodOptions([]);
        }
        setTransferUnderOptions([]);
        setSelectedTransferUnder(null);
      } else if (ground === "INTRA") {
        const [hodRes, underRes] = await Promise.all([
          fetchHodList("INTRA"),
          fetchTransferUnderList("INTRA"),
        ]);
        if (hodRes.success && Array.isArray(hodRes.data)) {
          setHodOptions(hodRes.data);
        } else {
          setHodOptions([]);
        }
        if (underRes.success && Array.isArray(underRes.data)) {
          setTransferUnderOptions(underRes.data);
        } else {
          setTransferUnderOptions([]);
        }
      }
    } catch (err) {
      console.error("Failed to load HOD / Transfer Under options:", err);
    } finally {
      setDynamicLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDynamicLists(transferGround);
  }, [transferGround, loadDynamicLists]);

  const handleTransferGroundChange = (val: string) => {
    setTransferGround(val);
    setSelectedHod(null);
    setSelectedTransferUnder(null);
    setErrors((prev) => ({ ...prev, transferGround: "", hod: "", transferUnder: "" }));
  };

  const handlePeriodValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numeric = e.target.value.replace(/[^0-9]/g, "");
    if (numeric.length > 2) {
      toast.warning("Please enter a smaller value (maximum 2 digits).");
      return;
    }
    setTransferPeriodValue(numeric);
  };

  const computedDates =
    reasonForTransfer === "TEM"
      ? calculateTransferDates(parseInt(transferPeriodValue, 10) || 0, transferPeriodUnit)
      : null;

  // Form Validation & Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    if (!selectedHod) {
      newErrors.hod = "Please select HOD / Unit destination.";
    }
    if (transferGround === "INTRA" && !selectedTransferUnder) {
      newErrors.transferUnder = "Please select Transfer Under for Intra Department transfer.";
    }
    if (!remarks.trim()) {
      newErrors.remarks = "Please enter remarks.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return;
    }

    try {
      setSubmitting(true);
      const isTemp = reasonForTransfer === "TEM";

      const payload = {
        TransferGround: transferGround,
        NameOfHODUnit:
          selectedHod?.HODEmpID || selectedHod?.EmpID || selectedHod?.NameOfHODUnit || "",
        Designation:
          selectedHod?.Designation ||
          employeeInfo.DesignationNameOfPostHeldPresently ||
          "",
        Department: selectedHod?.Department || "",
        TransferUnder:
          transferGround === "INTRA"
            ? selectedTransferUnder?.TransferUnderEmpID ||
              selectedTransferUnder?.EmpID ||
              selectedTransferUnder?.TransferUnder ||
              ""
            : "",
        TransferUnderDesignation:
          transferGround === "INTRA"
            ? selectedTransferUnder?.Designation || ""
            : "",
        ReasonForTransfer: reasonForTransfer,
        Remarks: remarks.trim(),
        TransferPeriod: isTemp ? parseInt(transferPeriodValue, 10) || 0 : 0,
        YearMonthDays: isTemp ? transferPeriodUnit : "",
      };

      const res = await createSelfTransferRequest(payload);
      if (res.success) {
        setSuccessMessage(
          "Request sent for approval"
        );
        setSuccessModalVisible(true);
      } else {
        toast.error(res.message || "Failed to submit self transfer request.");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" style={{ color: "#4F46E5" }} role="status" />
          <p className="text-muted mt-2">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header (Reimbursement style) */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 fw-bold">Create Self Transfer Request</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.selfTransfer.list}>Self Transfer</Link>
                </li>
                <li className="breadcrumb-item active text-primary">New Application</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <Link
              to={routes.selfTransfer.list}
              className="btn btn-outline-secondary rounded-pill px-3 shadow-sm border-2 fw-medium"
            >
              <i className="ti ti-arrow-left me-1" />
              Back to Requests
            </Link>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section 1: Transfer Ground */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-arrows-exchange text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Transfer Ground</h5>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">
                    <span className="text-danger">* </span>Transfer Ground
                  </label>
                  <select
                    className="form-select"
                    value={transferGround}
                    onChange={(e) => handleTransferGroundChange(e.target.value)}
                  >
                    {transferGroundOptions.map((g: any, i: number) => {
                      const val = g.TransferGround || g.value || (i === 0 ? "INTRA" : "INTER");
                      const text =
                        g.TransferGroundText ||
                        g.label ||
                        (val === "INTRA" ? "Intra Department" : "Inter Department");
                      return (
                        <option key={`${val}-${i}`} value={val}>
                          {text}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Employee Information */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-user text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Employee Information</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-muted small mb-1">Employee Code</label>
                  <div className="fw-semibold text-dark font-monospace">
                    {employeeInfo.EmpID || "-"}
                  </div>
                </div>
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-muted small mb-1">Name</label>
                  <div className="fw-semibold text-dark">
                    {employeeInfo.EmployeeName || "-"}
                  </div>
                </div>
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-muted small mb-1">
                    Designation/Name Of Post Held Presently
                  </label>
                  <div className="fw-semibold text-dark">
                    {employeeInfo.DesignationNameOfPostHeldPresently || "-"}
                  </div>
                </div>
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-muted small mb-1">
                    Division/Unit/Office in which the Employee is working
                  </label>
                  <div className="fw-semibold text-dark">
                    {employeeInfo.DivisionUnitOfficeInWhichTheEmployee || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Destination & Details */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-map-pin text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Destination &amp; Details</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                {/* HOD Destination */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">
                    <span className="text-danger">* </span>Name of HOD/Unit (Destination)
                  </label>
                  <select
                    className={`form-select ${errors.hod ? "is-invalid" : ""}`}
                    value={
                      selectedHod
                        ? selectedHod.HODEmpID || selectedHod.EmpID || selectedHod.NameOfHODUnit || ""
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      const found = hodOptions.find(
                        (h: any) =>
                          String(h.HODEmpID || h.EmpID || h.NameOfHODUnit) === String(val)
                      );
                      setSelectedHod(found || null);
                      setErrors((prev) => ({ ...prev, hod: "" }));
                    }}
                    disabled={dynamicLoading}
                  >
                    <option value="">Select HOD / Destination Unit</option>
                    {hodOptions.map((h: any, idx: number) => {
                      const key = h.HODEmpID || h.EmpID || h.NameOfHODUnit || idx;
                      const name =
                        h.NameOfHOD || h.HODName || h.NameOfHODUnit || "Unknown HOD";
                      const code = h.HODEmpID || h.EmpID ? ` (${h.HODEmpID || h.EmpID})` : "";
                      return (
                        <option key={key} value={key}>
                          {name}
                          {code}
                        </option>
                      );
                    })}
                  </select>
                  {errors.hod && <div className="invalid-feedback">{errors.hod}</div>}
                  {selectedHod && (
                    <div
                      className="d-inline-flex align-items-center gap-1 rounded-2 px-2 py-1 mt-2 small"
                      style={{ backgroundColor: "rgba(171, 31, 36, 0.08)", color: "#ab1f24" }}
                    >
                      <i className="ti ti-info-circle" />
                      <span>
                        {selectedHod.Department || selectedHod.Designation || "HOD Selected"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Transfer Under (Intra Department ONLY) */}
                {transferGround === "INTRA" && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">
                      <span className="text-danger">* </span>Transfer Under
                    </label>
                    <select
                      className={`form-select ${errors.transferUnder ? "is-invalid" : ""}`}
                      value={
                        selectedTransferUnder
                          ? selectedTransferUnder.TransferUnderEmpID ||
                            selectedTransferUnder.EmpID ||
                            selectedTransferUnder.TransferUnder ||
                            ""
                          : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        const found = transferUnderOptions.find(
                          (u: any) =>
                            String(u.TransferUnderEmpID || u.EmpID || u.TransferUnder) ===
                            String(val)
                        );
                        setSelectedTransferUnder(found || null);
                        setErrors((prev) => ({ ...prev, transferUnder: "" }));
                      }}
                      disabled={dynamicLoading}
                    >
                      <option value="">Select Transfer Under</option>
                      {transferUnderOptions.map((u: any, idx: number) => {
                        const key =
                          u.TransferUnderEmpID || u.EmpID || u.TransferUnder || idx;
                        const name =
                          u.TransferUnderEmployeeName ||
                          u.Name ||
                          u.TransferUnderText ||
                          "Employee";
                        const code = u.TransferUnderEmpID || u.EmpID ? ` (${u.TransferUnderEmpID || u.EmpID})` : "";
                        return (
                          <option key={key} value={key}>
                            {name}
                            {code}
                          </option>
                        );
                      })}
                    </select>
                    {errors.transferUnder && (
                      <div className="invalid-feedback">{errors.transferUnder}</div>
                    )}
                    {selectedTransferUnder && (
                      <div
                        className="d-inline-flex align-items-center gap-1 rounded-2 px-2 py-1 mt-2 small"
                        style={{ backgroundColor: "rgba(171, 31, 36, 0.08)", color: "#ab1f24" }}
                      >
                        <i className="ti ti-info-circle" />
                        <span>
                          {selectedTransferUnder.Designation ||
                            selectedTransferUnder.Department ||
                            "Employee Selected"}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Info Grid */}
              <div
                className="mt-4 p-3 rounded-3"
                style={{ backgroundColor: "#F8F9FF", border: "1px solid #EAECF4" }}
              >
                <div className="row g-3">
                  <div className="col-md-4 col-sm-6">
                    <span className="text-muted small d-block">Category</span>
                    <strong className="text-dark small">{employeeInfo.Category || "Executive"}</strong>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <span className="text-muted small d-block">Pay Scale</span>
                    <strong className="text-dark small">{employeeInfo.PayScale || "-"}</strong>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <span className="text-muted small d-block">Customer Specific Status</span>
                    <strong className="text-dark small">{employeeInfo.CustomerSpecificStatus || "-"}</strong>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <span className="text-muted small d-block">Employment Status</span>
                    <span className="badge bg-success-subtle text-success border border-success-subtle small">
                      <span className="d-inline-block rounded-circle bg-success me-1" style={{ width: 6, height: 6 }} />
                      {employeeInfo.EmploymentStatus || "Active"}
                    </span>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <span className="text-muted small d-block">Employee subgroup</span>
                    <strong className="text-dark small">{employeeInfo.EmployeeSubGroup || "-"}</strong>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <span className="text-muted small d-block">Wing/ unit name</span>
                    <strong className="text-dark small">{employeeInfo.Wing || "-"}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Transfer Terms */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-calendar text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Transfer Terms</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">
                    <span className="text-danger">* </span>Reason For Transfer
                  </label>
                  <select
                    className="form-select"
                    value={reasonForTransfer}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReasonForTransfer(val);
                      if (val !== "TEM") {
                        setTransferPeriodValue("0");
                      }
                    }}
                  >
                    {reasonOptions.map((r: any, idx: number) => {
                      const val = r.ReasonForTransfer || r.Value || r.value || (idx === 0 ? "PER" : "TEM");
                      const text =
                        r.ReasonForTransferText ||
                        r.Ddtext ||
                        r.label ||
                        (val === "PER" ? "Permanent" : "Temporary");
                      return (
                        <option key={`${val}-${idx}`} value={val}>
                          {text}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Transfer Period Fields */}
                {reasonForTransfer === "TEM" ? (
                  <>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold small">Transfer Period</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="0"
                        value={transferPeriodValue}
                        onChange={handlePeriodValueChange}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold small">Period Unit</label>
                      <select
                        className="form-select"
                        value={transferPeriodUnit}
                        onChange={(e) => setTransferPeriodUnit(e.target.value)}
                      >
                        {periodUnitOptions.map((p: any, idx: number) => {
                          const val = p.TransferPeriod || p.Value || p.value || (idx === 0 ? "YEAR" : idx === 1 ? "MONTH" : "DAY");
                          const text =
                            p.TransferPeriodText || p.Ddtext || p.label || (val === "YEAR" ? "Year(s)" : val === "MONTH" ? "Month(s)" : "Day(s)");
                          return (
                            <option key={`${val}-${idx}`} value={val}>
                              {text}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Computed Dates for Temporary */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">From Date</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        readOnly
                        value={computedDates ? computedDates.from : ". ."}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">To Date</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        readOnly
                        value={computedDates ? computedDates.to : ". ."}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">Transfer Period</label>
                      <input
                        type="text"
                        className="form-control bg-light text-muted"
                        value="0"
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">From Date</label>
                      <input
                        type="text"
                        className="form-control bg-light text-muted"
                        value=". ."
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold small text-muted">To Date</label>
                      <input
                        type="text"
                        className="form-control bg-light text-muted"
                        value=". ."
                        readOnly
                        disabled
                      />
                    </div>
                  </>
                )}

                {/* Nodal Remarks (Disabled on creation) */}
                <div className="col-12 mt-3">
                  <label className="form-label fw-semibold small text-muted">
                    Nodal Remarks regarding availability of vacancy and feasibility of transfer
                  </label>
                  <textarea
                    className="form-control bg-light text-muted"
                    rows={3}
                    readOnly
                    disabled
                    placeholder=""
                    value=""
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Remark */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 me-2"
                style={{ width: 32, height: 32, backgroundColor: "rgba(171, 31, 36, 0.1)" }}
              >
                <i className="ti ti-message-dots text-danger" style={{ color: "#ab1f24" }} />
              </div>
              <h5 className="card-title mb-0 fw-bold text-dark">Remark</h5>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-12">
                  <label className="form-label fw-semibold small">
                    <span className="text-danger">* </span>Remark
                  </label>
                  <textarea
                    className={`form-control ${errors.remarks ? "is-invalid" : ""}`}
                    rows={4}
                    placeholder="Enter reason/remarks for transfer"
                    value={remarks}
                    onChange={(e) => {
                      setRemarks(e.target.value);
                      setErrors((prev) => ({ ...prev, remarks: "" }));
                    }}
                  />
                  {errors.remarks && (
                    <div className="invalid-feedback">{errors.remarks}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form Action Buttons */}
          <div className="d-flex justify-content-end align-items-center gap-2 mb-5">
            <Link to={routes.selfTransfer.list} className="btn btn-outline-secondary rounded-pill px-4 py-2">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-primary rounded-pill px-4 py-2 shadow-sm d-flex align-items-center fw-bold border-0"
              style={{ backgroundColor: "#ab1f24" }}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ti ti-send me-1" />
                  Send for Approval
                </>
              )}
            </button>
          </div>
        </form>

        {/* Success Modal */}
        {successModalVisible && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
              <div className="modal-content border-0 shadow-lg rounded-4 text-center p-4">
                <div className="my-3">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 72,
                      height: 72,
                      backgroundColor: "rgba(34, 197, 94, 0.15)",
                    }}
                  >
                    <i className="ti ti-circle-check fs-1 text-success" />
                  </div>
                </div>
                <h5 className="fw-bold text-dark mb-2">Success</h5>
                <p className="text-muted mb-4">{successMessage}</p>
                <button
                  type="button"
                  className="btn btn-danger rounded-pill w-100 py-2 shadow-sm fw-bold border-0"
                  style={{ backgroundColor: "#ab1f24" }}
                  onClick={() => {
                    setSuccessModalVisible(false);
                    navigate(routes.selfTransfer.list);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfTransferCreate;
